/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { AbstractControl, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { BaseComponent } from '../../base.component';
import { Component, ComponentRef, EventEmitter, Input, Output, ViewChild, ViewContainerRef } from '@angular/core';
import { ConfirmPasswordComponent } from '../../confirmPassword/confirmPassword.component';
import { KeyStoreService, LanguageService, LogService, PGPService } from '../../../services';
import { takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-private-key',
    templateUrl: './privateKey.component.html',
    styleUrls: ['./privateKey.component.scss'],
})

export class PrivateKeyComponent extends BaseComponent {
    /**
     * Reference to new service container.
     */
    @ViewChild('modalContainer', { read: ViewContainerRef, static: true })
    public modalContainer: ViewContainerRef;

    /**
     * Option to disable the key and download.
     */
    @Input() public disabled: boolean = false;

    /**
     * The id of our private key.
     */
    @Input() public privateKeyId: string;

    /**
     * The name of our private key download file.
     */
    @Input() public keyName: string;

    /**
     * Take in the user's password.
     */
    @Input() public userPassword: string;
    /**
     * Align the content for different uses.
     */
    @Input() public contentAlignment: 'left' | 'center' | 'right' = 'left';

    /**
     * Show or hide the intro message.
     */
    @Input() public showIntroMessage: boolean = true;

    /**
     * Give another component the option to close this modal.
     */
    @Input() public closeModalInternally: boolean = true;

    /**
     * Option to download org key instead of user key.
     */
    @Input() public getOrgKey: boolean = false;

    /**
     * Output once the key has been downloaded.
     */
    @Output() public keyDecrypted: EventEmitter<any> = new EventEmitter();

    /**
     * Private key download state.
     */
    public privateKeyDecrypted: boolean = false;

    /**
     * New organisation form group.
     */
    public passwordForm: FormGroup;

    /**
     * Confirm password component reference.
     */
    public componentReference: ComponentRef<any>;

    constructor(
        private formBuilder: FormBuilder,
        private keyStoreService: KeyStoreService,
        private languageService: LanguageService,
        private logService: LogService,
        private pgpService: PGPService
    ) {
        super();

        this.setUpFormGroup();
    }

    /**
     * Helper function to get user password if not supplied.
     */
    public getPrivateKey(): void {
        if (!this.disabled) {
            if (!this.userPassword) {
                const confirmPasswordModal: ComponentRef<any> = this.modalContainer.createComponent(ConfirmPasswordComponent);
                confirmPasswordModal.instance.message = 'onboard_organization_private_key_message';
                confirmPasswordModal.instance.closeModalInternally = this.closeModalInternally;
                confirmPasswordModal.instance.getOrgKey = this.getOrgKey;
                confirmPasswordModal.instance.cancelCallBack = (): void => this.componentReference.destroy();
                confirmPasswordModal.instance.callBack = (password: string): void => {
                    const apiCall: any = this.getApiCall(password);
                    this.decryptPrivateKey(apiCall);
                };

                this.componentReference = confirmPasswordModal;
            } else {
                const apiCall: any = this.getApiCall(this.userPassword);
                this.decryptPrivateKey(apiCall);
            }
        }
    }

    /**
     * Helper function to get correct API call.
     * @param userPassword
     * @private
     */
    private getApiCall(userPassword: string): any {
        const privateKey: any = this.keyStoreService.getPrivateKeyFromKeystore();
        if (this.getOrgKey) {
            const orgPrivateKey: any = this.keyStoreService.getPrivateKeyFromKeystore(this.getOrgKey);

            return this.pgpService.getDecryptedOrgKeyWithUserPassword(userPassword, privateKey, orgPrivateKey);
        } else {
            return this.pgpService.decryptPrivateKeyWithPassword(userPassword, privateKey, true);
        }
    }

    /**
     * Get private key.
     * @param apiCall
     * @private
     */
    private decryptPrivateKey(apiCall: any): void {
        apiCall.pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (decryptedKey: any): void => {
                    this.privateKeyDecrypted = true;
                    this.keyDecrypted.next(decryptedKey);
                },
                error: (error: any): void => {
                    const passwordField: AbstractControl = this.passwordForm.get('password');
                    passwordField.setErrors({ 'decryptFail': `${this.languageService.translate('incorrect_password')}` });
                    this.logService.error('PrivateKeyComponent: decryptPrivateKey', error);
                },
                complete: (): void => {
                    if (this.componentReference) {
                        this.componentReference.destroy();
                    }
                }
            });
    }

    /**
     * Set up password form
     * @private
     */
    private setUpFormGroup(): void {
        this.passwordForm = this.formBuilder.group({
            password: [null, [Validators.required]]
        });
    }
}
